
/**
 * Import Dependency
 */

import Request from '@/models/Request'

import { deepmerge } from '@/../shared/helpers/object'

import { reasonOptions, reasonOptionsByStatusAndReason, visibleRequestsByRealisationStatuses } from '@/config'

/**
 * This list does not change, so it's kept outside of the state object
 */
const statusOptions = [
  // id: 0 = onvolledig
  // id: 1 = automatisch afgewezen
  {
    id: 2,
    name: 'open',
    label: 'opnenen',
    icon: 'file-earmark-fill',
    variant: 'dark',
  },
  {
    id: 3,
    name: 'geaccepteerd',
    label: 'accepteren',
    icon: 'check-circle-fill',
    variant: 'success',
  },
  {
    id: 4,
    name: 'afgewezen',
    label: 'afwijzen',
    icon: 'x-circle-fill',
    variant: 'danger',
  },
  {
    id: 5,
    name: 'verwijderd',
    label: 'verwijderen',
    icon: 'trash-fill',
    variant: 'warning',
  },
]


/**
 * Import API
 */

/**
 * Declare Variable
 */
 const state = {
  /**
   * Locks the UI while loading / refreshing / sending data
   */
  loading: false,
  lastRefresh: '',

  /**
   * The search term in the list view
   */
  searchterm: '',
  currentPage: 1,

  /**
   * List of requests
   */
  requests: [],

  activeStatusId: 2, // open

  /**
   * Keep track of the last viewed request (in this session)
   */
  lastOpenedRequest: null,

  /**
   * Keep track of the currently viewed request
   */
  currentlyOpenedRequest: null,

  filteredRequestsByRealisationStatus: [],
}

const getters = {
  loading: state => state.loading,
  lastRefresh: state => state.lastRefresh,

  searchterm: state => state.searchterm,
  currentPage: state => state.currentPage,

  lastOpenedRequest: state => state.lastOpenedRequest,
  currentlyOpenedRequest: state => state.currentlyOpenedRequest,
  hasCurrentlyOpenedRequest: state => state.currentlyOpenedRequest !== null,

  /**
   * List
   */
  requests: state => state.requests.sort((a, b) => a.counter > b.counter ? -1: 1),
  hasRequests: state => state.requests.length !== 0,
  requestsByStatus: (state, getters) => ({ status }) => getters.requests.filter(request => request.status === status),
  requestsByActiveStatus: (state, getters) => getters.requestsByStatus({ status: state.activeStatusId }),
   /**
   * Filtering requests connected to realisation with given status
   */
  requestsByRealisationStatus: state => state.filteredRequestsByRealisationStatus,

  /**
   * Requests without coordinates
   */
  requestsWithoutCoordinates: (state, getters) => getters.requests.filter(request => request.coordinates.lat === null && request.coordinates.nomatch !== true),

  /**
   * Detail
   */
  hasRequest: state => ({ uuid }) => {
    return Array.isArray(state.requests) && state.requests.filter(request => request.uuid === uuid).length > 0
  },
  requestByUuid: state => ({ uuid }) => {
    let request = state.requests.filter(request => request.uuid === uuid)
    return request.length ? request[0] : null
  },

  requestCountByPostalcode: state => ({ postalcode }) => {
    return state.requests.filter(request => request.search.postalcode === postalcode).length
  },
  requestCountByNumberplate: state => ({ numberplate }) => {
    return state.requests.filter(request => request.search.numberplate === numberplate).length
  },
  requestCountByCity: state => ({ city }) => {
    return state.requests.filter(request => request.search.city === city.toLowerCase()).length
  },

  /**
   * Status options (kept outside of store)
   */
  statusOptions: () => statusOptions,
  statusOptionById: () => ({ id }) => {
    let option = statusOptions.filter(option => option.id === id)
    return option.length ? option[0] : {}
  },
  activeStatus: (state, getters) => getters.statusOptionById({ id: state.activeStatusId }),
  activeStatusId: state => state.activeStatusId,
  activeStatusName: (state, getters) => getters.activeStatusOption.name,

  // TODO: make data available in multiple formats (e.g. array and nested object) to make data subsets more directly accessible
  reasonOptions: () => reasonOptions,
  reasonOptionsByStatusAndReason: () => reasonOptionsByStatusAndReason,
}
const actions = {
  loadRequestRecordsFromCBAPI: async function(
    { state, commit }, 
    { codes, progressCallback, at = null }
  ) {
    if (state.loading) return

    commit('setLoadingState', { loading: true })

    let records = await this.$cbapi.utility.paginatedAPICall({
      apiCall: this.$cbapi.requests.getCollectionPage,
      params: {
        codes,
        at,
      },
      progressCallback
    })

    commit('setRecords', {
      records: records.map(
        record => {
          // record.data.ts = record.ts || null
          return Object.freeze(
            new Request({ 
              data: record, 
              ref: null
            })
          )
        }
      )
    })
    commit('setLoadingState', { loading: false })
  },

  syncRequestRecords: async function(
    { state, commit }, 
    { codes, progressCallback, at = null, fromTimeStamp = null }
  ) {
    if (state.loading) return null

    commit('setLoadingState', { loading: true })

    let records = await this.$cbapi.utility.paginatedAPICall({
      apiCall: this.$cbapi.requests.getCollectionPage,
      params: {
        codes, at, fromTimeStamp
      },
      progressCallback
    })

    records = records.map(
      record => {
        // record.data.ts = record.ts || null
        return Object.freeze(
          new Request({ 
            data: record, 
            ref: null
          })
        )
      }
    )
    
    commit('replaceOrAddRequests', {
      records
    })
  
    commit('setLoadingState', { loading: false })

    return records
  },

  loadRequests: async ({ state, commit }, { token }) => {

    if (state.loading) return

    commit('setLoadingState', { loading: true })
    console.log("USING OLD API")
    const api = await fetch('/api/list', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (! api.ok) {
      commit('setLoadingState', { loading: false })
    } else {
      const response = await api.json()

      const requests = response.data.requests.map(request => {
        return Object.freeze( new Request({ data: request.data, ref: request.ref['@ref'].id }) )
      })

      commit('setRequests', {
        requests,
      })
      commit('setLoadingState', { loading: false })
    }
  },
  loadRequestsByCode: async ({ commit }, { token, code }) => {
    console.log("USING OLD API")
    const api = await fetch('/api/list', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        codes: [code],
      }),
    })
    if (! api.ok) {
      throw new Error('failed')
    }

    const response = await api.json()
    const requests = response.data.requests.map(request => {
      return Object.freeze( new Request({ data: request.data, ref: request.ref['@ref'].id }) )
    })

    commit('replaceRecordsByCode', {
      requests, code,
    })
  },
  loadRequestCoordinates({ commit }, { coordinates, ref }) {
    commit('updateRequestCoordinates', { coordinates, ref })
  },
  loadRequestsByRealisationStatus({ state, commit, rootGetters }) {
    // In config file and used for toggle(s) in map legend
    const statuses = visibleRequestsByRealisationStatuses
    // Only invisible (false) statuses
    const filteredStatuses = Object.keys(statuses).filter(status => !statuses[status])
    const records = new Set()

    // Find all realisations with active status (true) found in the filteredStatuses and
    // put it's connected requests into records (Set)
    rootGetters['realisation/records'].map(record => {
      filteredStatuses.forEach(status => {
        if (record.status[status]) {
          record.requestUuids.forEach(uuid => records.add(uuid))
        }
      })
    })
    // Filter requests based on request uuid's found in records (Set)
    const requests = records.size
      ? state.requests.filter(record => !Array.from(records).includes(record.uuid))
      : state.requests

    commit('updateRequestByRealisationStatus', { requests })
  },
}
const mutations = {
  setLoadingState(state, { loading }) {
    state.loading = loading
  },
  setRecords(state, { records }) {
    state.requests = records
    const date = new Date(Date.now())
    state.lastRefresh = `${(`0${date.getHours()}`.slice(-2))}:${(`0${date.getMinutes()}`.slice(-2))}:${(`0${date.getSeconds()}`.slice(-2))}`
  },
  setRequests(state, { requests }) {
    state.requests = requests
    const date = new Date(Date.now())
    state.lastRefresh = `${(`0${date.getHours()}`.slice(-2))}:${(`0${date.getMinutes()}`.slice(-2))}:${(`0${date.getSeconds()}`.slice(-2))}`
  },
  replaceRecordsByCode(state, { requests, code }) {
    state.requests = (state.requests || [])
      .filter(record => record.getMunicipality() !== code)
      .concat(requests)

    const date = new Date(Date.now())
    state.lastRefresh = `${(`0${date.getHours()}`.slice(-2))}:${(`0${date.getMinutes()}`.slice(-2))}:${(`0${date.getSeconds()}`.slice(-2))}`
  },

  setActiveStatus(state, { status }) {
    state.activeStatusId = status
  },
  setSearchterm(state, { term }) {
    state.searchterm = term
  },
  setCurrentPage(state, page) {
    state.currentPage = page
  },

  setLastOpenedRequest(state, { uuid }) {
    state.lastOpenedRequest = uuid
  },
  setCurrentlyOpenedRequest(state, { uuid }) {
    state.currentlyOpenedRequest = uuid
  },

  /**
   * Update a request in the local store
   */
  updateRequest(state, { ref, data }) {
    // Get current request entry by ref
    let requestIndex = state.requests.findIndex(request => request.ref === ref)
    if (requestIndex < 0) return

    // Obtain the current Request object
    let request = state.requests[requestIndex]

    // Keep track of tag changes
    // TODO: Implement a better solution when switching to Central API / WebSocket
    data = data || {}
    if (! data.Tags) {
      data.Tags = request.getTags()
    }

    // Create a new Request object
    request = Object.freeze( 
      new Request({
        ref,
        data: deepmerge(request.raw, data),
      })
    )

    // Out with the old, in with the new
    state.requests.splice(requestIndex, 1, request)
  },

  replaceOrAddRequests(state, { records }) {
    for (let i = 0; records.length > i; i++) {
      let request = records[i]

      let recordIndex = state.requests
        .findIndex(current => current.uuid === request.uuid)

      if (recordIndex !== -1) {

        // Records are frozen, so if the TS matches the record was already synced
        if (request.getSyncedTS() === state.requests[recordIndex].getSyncedTS()) {
          return 
        }

        state.requests.splice(
          recordIndex,
          1,
          request
        )
      } else {
        state.requests.push(
          request
        )
      }
    }
    
    const date = new Date(Date.now())
    state.lastRefresh = `${(`0${date.getHours()}`.slice(-2))}:${(`0${date.getMinutes()}`.slice(-2))}:${(`0${date.getSeconds()}`.slice(-2))}`
  },

  replaceRequest(state, { request }) {
    let recordIndex = state.requests
      .findIndex(current => current.uuid === request.uuid)

    if (recordIndex !== -1) {
      state.requests.splice(
        recordIndex,
        1,
        Object.freeze(
          new Request({
            ref: null,
            data: request,
          })
        ),
      )
    }
  },

  updateRequestCoordinates(state, { coordinates, ref }) {
    const requestIndex = state.requests.findIndex(request => request.ref === ref)
    if (requestIndex !== -1) {
      state.requests[requestIndex].coordinates = coordinates
    }
  },

  // This is PRE-CBAPI Method
  updateRequestComments(state, { ref, comments }) {
    let index = state.requests.findIndex(record => record.ref === ref)
    if (index < 0) return

    let request = state.requests[index]

    request.comments = comments
  },

  updateRequestByRealisationStatus (state, { requests }) {
    state.filteredRequestsByRealisationStatus = requests
  },
}

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}

